import React, { useMemo } from 'react';
import Link from 'next/link';
import { Anomaly, Device, FLOW_STALENESS_THRESHOLD_MS } from '../types';
import { useFlowThresholds } from '../hooks/useFlowThresholds';
import { isModberryDevice } from '../utils/deviceTypeHelpers';
import { shortDeviceName } from '../utils/deviceNameUtils';

interface ImmediateAttentionItem {
  id: string;
  deviceId: string;
  farmName: string;
  deviceName: string;
  issueType: 'offline_commissioned' | 'liquid_blockage';
  severity: Anomaly['severity'];
  description: string;
  suggestion?: string;
  detectedAt: string;
  offlineDurationLabel?: string;
}

interface ImmediateAttentionProps {
  anomalies: Anomaly[] | null;
  devices: Device[] | null;
}

function formatOfflineDuration(ms: number): string {
  const minutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return 'Just now';
}

function getOfflineDurationLabel(device: Device): string | undefined {
  if (device.offlineDurationMs && device.offlineDurationMs > 0) {
    return formatOfflineDuration(device.offlineDurationMs);
  }
  if (device.offlineSince) {
    const since = new Date(device.offlineSince).getTime();
    const now = Date.now();
    const diff = now - since;
    if (diff > 0) {
      return formatOfflineDuration(diff);
    }
  }
  return undefined;
}

function buildImmediateAttentionItems(
  anomalies: Anomaly[],
  devices: Device[],
  criticalThreshold: number,
): ImmediateAttentionItem[] {
  const deviceMap = new Map<string, Device>();
  for (const device of devices) {
    deviceMap.set(device.deviceId, device);
  }

  const items: ImmediateAttentionItem[] = [];
  const deviceIdsWithBlockageAnomaly = new Set<string>();

  // 1) Anomaly-based detection (from DB anomalies table)
  for (const anomaly of anomalies) {
    if (anomaly.acknowledged) { continue; }

    if (anomaly.anomalyType === 'liquid_blockage') {
      deviceIdsWithBlockageAnomaly.add(anomaly.deviceId);
      const device = deviceMap.get(anomaly.deviceId);
      items.push({
        id: anomaly.id,
        deviceId: anomaly.deviceId,
        farmName: shortDeviceName(device?.name || anomaly.farmName || 'Unknown Farm'),
        deviceName: shortDeviceName(device?.name || anomaly.deviceId),
        issueType: 'liquid_blockage',
        severity: anomaly.severity,
        description: anomaly.description,
        suggestion: anomaly.suggestion,
        detectedAt: anomaly.detectedAt,
      });
    } else if (anomaly.anomalyType === 'inactivity_timeout') {
      const device = deviceMap.get(anomaly.deviceId);
      if (device?.commissionedAt) {
        items.push({
          id: anomaly.id,
          deviceId: anomaly.deviceId,
          farmName: shortDeviceName(device.name || anomaly.farmName || 'Unknown Farm'),
          deviceName: shortDeviceName(device.name || anomaly.deviceId),
          issueType: 'offline_commissioned',
          severity: anomaly.severity,
          description: anomaly.description,
          suggestion: anomaly.suggestion,
          detectedAt: anomaly.detectedAt,
          offlineDurationLabel: getOfflineDurationLabel(device),
        });
      }
    }
  }

  // 2) Device-metrics-based blockage detection (real-time, same logic as DeviceCard)
  //    Catches blockages even when the anomaly pipeline hasn't created a DB record yet.
  for (const device of devices) {
    if (deviceIdsWithBlockageAnomaly.has(device.deviceId)) { continue; }
    if (!isModberryDevice(device)) { continue; }
    if (!device.commissionedAt) { continue; }
    if (device.sprinklingSchedulerEnabled !== true) { continue; }
    if (device.metrics?.status === 'offline') { continue; }

    const effectiveFlow = device.metrics?.liquidToMixtankFlowRate ?? device.metrics?.dosingFlowRate;
    if (effectiveFlow === undefined || effectiveFlow === null) { continue; }

    // Skip stale data — if last telemetry check is too old, flow data is unreliable
    if (device.lastTelemetryCheck) {
      const lastCheck = new Date(device.lastTelemetryCheck).getTime();
      if (Date.now() - lastCheck > FLOW_STALENESS_THRESHOLD_MS) { continue; }
    }

    if (effectiveFlow <= criticalThreshold) {
      items.push({
        id: `metrics-blockage-${device.deviceId}`,
        deviceId: device.deviceId,
        farmName: shortDeviceName(device.name || device.farmName || 'Unknown Farm'),
        deviceName: shortDeviceName(device.name || device.deviceId),
        issueType: 'liquid_blockage',
        severity: 'critical',
        description: `Critical liquid flow blockage detected. Flow rate: ${effectiveFlow.toFixed(1)} L/min (threshold: ${criticalThreshold} L/min). MgCl filter requires immediate service.`,
        suggestion: 'Clean or replace the MgCl filter immediately — liquid flow is severely restricted',
        detectedAt: new Date().toISOString(),
      });
    }
  }

  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const issueOrder: Record<string, number> = { liquid_blockage: 0, offline_commissioned: 1 };

  items.sort((a, b) => {
    const issueA = issueOrder[a.issueType] ?? 2;
    const issueB = issueOrder[b.issueType] ?? 2;
    if (issueA !== issueB) { return issueA - issueB; }

    const sevA = severityOrder[a.severity] ?? 4;
    const sevB = severityOrder[b.severity] ?? 4;
    if (sevA !== sevB) { return sevA - sevB; }

    return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime();
  });

  return items;
}

interface IssueStyle {
  label: string;
  icon: string;
  badgeBg: string;
  badgeText: string;
  textPrimary: string;
  textSecondary: string;
  textSuggestion: string;
  hoverBg: string;
  linkHover: string;
  durationText: string;
}

function getIssueStyle(issueType: ImmediateAttentionItem['issueType']): IssueStyle {
  switch (issueType) {
    case 'liquid_blockage':
      return {
        label: 'Liquid Blockage',
        icon: '\u{1F6A8}',
        badgeBg: 'bg-amber-200',
        badgeText: 'text-amber-900',
        textPrimary: 'text-amber-900',
        textSecondary: 'text-amber-800',
        textSuggestion: 'text-amber-700',
        hoverBg: 'hover:bg-amber-100/50',
        linkHover: 'hover:text-amber-700',
        durationText: 'text-amber-700',
      };
    case 'offline_commissioned':
      return {
        label: 'Offline',
        icon: '\u{1F534}',
        badgeBg: 'bg-purple-200',
        badgeText: 'text-purple-900',
        textPrimary: 'text-purple-900',
        textSecondary: 'text-purple-800',
        textSuggestion: 'text-purple-700',
        hoverBg: 'hover:bg-purple-100/50',
        linkHover: 'hover:text-purple-700',
        durationText: 'text-purple-700',
      };
    default:
      return {
        label: 'Issue',
        icon: '\u{26A0}\u{FE0F}',
        badgeBg: 'bg-red-200',
        badgeText: 'text-red-900',
        textPrimary: 'text-red-900',
        textSecondary: 'text-red-800',
        textSuggestion: 'text-red-700',
        hoverBg: 'hover:bg-red-100/50',
        linkHover: 'hover:text-red-700',
        durationText: 'text-red-700',
      };
  }
}

export const ImmediateAttention: React.FC<ImmediateAttentionProps> = ({
  anomalies,
  devices,
}) => {
  const { thresholds } = useFlowThresholds();
  const liquidFlowThreshold = thresholds.find(t => t.thresholdName === 'liquid_input_flow');
  const criticalThreshold = liquidFlowThreshold?.criticalThreshold ?? 10;

  const items = useMemo(() => {
    if (!anomalies || !devices) { return []; }
    return buildImmediateAttentionItems(anomalies, devices, criticalThreshold);
  }, [anomalies, devices, criticalThreshold]);

  if (items.length === 0) { return null; }

  const blockageCount = items.filter((i) => i.issueType === 'liquid_blockage').length;
  const offlineCount = items.filter((i) => i.issueType === 'offline_commissioned').length;

  return (
    <div className="bg-gray-50 border-l-4 border-red-500 rounded-lg shadow mb-8">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl" role="img" aria-label="warning">
              {'\u{1F6A8}'}
            </span>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Immediate Attention Required
              </h2>
              <p className="text-sm text-gray-700 mt-0.5">
                {blockageCount > 0 && (
                  <span className="text-amber-800 font-medium">
                    {blockageCount} liquid blockage{blockageCount !== 1 ? 's' : ''}
                  </span>
                )}
                {blockageCount > 0 && offlineCount > 0 && <span> &middot; </span>}
                {offlineCount > 0 && (
                  <span className="text-purple-800 font-medium">
                    {offlineCount} device{offlineCount !== 1 ? 's' : ''} offline
                  </span>
                )}
              </p>
            </div>
          </div>
          <span className="text-sm font-medium text-gray-800 bg-gray-200 px-3 py-1 rounded-full">
            {items.length} issue{items.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Items */}
      <div className="divide-y divide-gray-200">
        {items.map((item) => {
          const style = getIssueStyle(item.issueType);
          return (
            <div
              key={item.id}
              className={`px-6 py-4 ${style.hoverBg} transition-colors`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <span className="text-xl mt-0.5 flex-shrink-0" role="img" aria-label={item.issueType}>
                    {style.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        href={`/device/${item.deviceId}`}
                        className={`font-semibold ${style.textPrimary} ${style.linkHover} truncate`}
                      >
                        {item.farmName}
                      </Link>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.badgeBg} ${style.badgeText}`}>
                        {style.label}
                      </span>
                      {item.offlineDurationLabel && (
                        <span className={`text-xs ${style.durationText} font-medium`}>
                          {item.offlineDurationLabel}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${style.textSecondary} mt-1 line-clamp-2`}>
                      {item.description}
                    </p>
                    {item.suggestion && (
                      <p className={`text-xs ${style.textSuggestion} mt-1.5 flex items-start gap-1`}>
                        <span className="flex-shrink-0 mt-0.5">{'\u{1F527}'}</span>
                        <span className="font-medium">{item.suggestion}</span>
                      </p>
                    )}
                  </div>
                </div>

                <Link
                  href={`/device/${item.deviceId}`}
                  className={`flex-shrink-0 text-sm font-medium ${style.textSuggestion} ${style.linkHover} whitespace-nowrap`}
                >
                  View details {'→'}
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImmediateAttention;
